declare global {
  interface Window {
    __API_BASE_URL__?: string;
  }
}

/** Pulumi / raw K8s manifests use tokens like ``__VITE_API_URL__`` until rendered. */
function isUnrenderedTemplateToken(value: string): boolean {
  return /^__[A-Z_][A-Z0-9_]*__$/.test(value.trim());
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function isLocalDevHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function isLocalApiOrigin(url: string): boolean {
  try {
    return isLocalDevHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

function inferApiOriginFromHostname(hostname: string): string | null {
  if (
    hostname === "thyra-ai.com" ||
    hostname === "www.thyra-ai.com" ||
    hostname === "app.thyra-ai.com"
  ) {
    return "https://api.thyra-ai.com";
  }
  if (hostname === "app-dev.thyra-ai.com") {
    return "https://api-dev.thyra-ai.com";
  }
  return null;
}

function isLandingViteDev(port: string): boolean {
  return port === "3001" || port === "5173" || port === "5199";
}

function acceptApiOrigin(
  raw: string,
  allowLocalhostOrigin: boolean,
): string | null {
  const trimmed = raw.trim();
  if (!trimmed || isUnrenderedTemplateToken(trimmed)) return null;
  const normalized = trimmed.replace(/\/$/, "");
  if (!isAbsoluteHttpUrl(normalized)) return null;
  if (!allowLocalhostOrigin && isLocalApiOrigin(normalized)) return null;
  return normalized;
}

/**
 * FastAPI origin for landing fetches (e.g. ready-to-ship agents catalog).
 * On localhost Vite dev (port 3001), returns "" so requests use the dev-server
 * proxy and avoid Traefik load-balancer timeouts. Docker landing (port 3002) uses
 * http://localhost:8000 directly.
 */
export function getApiBaseUrl(): string {
  if (typeof window !== "undefined") {
    if (
      isLocalDevHost(window.location.hostname) &&
      isLandingViteDev(window.location.port)
    ) {
      return "";
    }
  }

  const allowLocalhostOrigin =
    typeof window !== "undefined" && isLocalDevHost(window.location.hostname);

  const candidates: string[] = [];
  if (typeof window !== "undefined") {
    candidates.push((window.__API_BASE_URL__ || "").trim());
  }
  candidates.push((import.meta.env.VITE_API_URL || "").trim());

  for (const raw of candidates) {
    const accepted = acceptApiOrigin(raw, allowLocalhostOrigin);
    if (accepted) return accepted;
  }

  if (typeof window !== "undefined") {
    const inferred = inferApiOriginFromHostname(window.location.hostname);
    if (inferred) return inferred;
  }

  return "http://localhost:8000";
}
